import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { useRBAC } from "../contexts/RBACContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Megaphone, AlertTriangle, Info, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";

export default function BulletinBoard({ user }) {
  const { hasPermission } = useRBAC();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    type: "info"
  });

  // Use dynamic RBAC permissions
  const canManage = hasPermission('bulletin_post', user.role);

  useEffect(() => {
    fetchBulletins();
  }, []);

  const fetchBulletins = async () => {
    try {
      const response = await axios.get(`${API}/bulletins`);
      setBulletins(response.data);
    } catch (error) {
      console.error("Failed to load bulletins");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      // Create bulletin first
      const response = await axios.post(`${API}/bulletins`, formData);
      const bulletinId = response.data.id;
      
      // Upload image if selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('file', imageFile);
        await axios.post(`${API}/bulletins/${bulletinId}/image`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Bulletin posted successfully");
      setShowCreateModal(false);
      setFormData({ title: "", content: "", type: "info" });
      setImageFile(null);
      setImagePreview(null);
      fetchBulletins();
    } catch (error) {
      toast.error("Failed to create bulletin");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await axios.patch(`${API}/bulletins/${selectedBulletin.id}`, formData);
      
      // Upload new image if selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('file', imageFile);
        await axios.post(`${API}/bulletins/${selectedBulletin.id}/image`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success("Bulletin updated");
      setShowEditModal(false);
      setImageFile(null);
      setImagePreview(null);
      fetchBulletins();
    } catch (error) {
      toast.error("Failed to update bulletin");
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPG, PNG, GIF, or WebP)");
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = async () => {
    if (selectedBulletin?.image_url) {
      try {
        await axios.delete(`${API}/bulletins/${selectedBulletin.id}/image`);
        toast.success("Image removed");
        fetchBulletins();
      } catch (error) {
        toast.error("Failed to remove image");
      }
    }
    setImageFile(null);
    setImagePreview(null);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/bulletins/${selectedBulletin.id}`);
      toast.success("Bulletin deleted");
      setShowDeleteDialog(false);
      setSelectedBulletin(null);
      fetchBulletins();
    } catch (error) {
      toast.error("Failed to delete bulletin");
    }
  };

  const openEditModal = (bulletin) => {
    setSelectedBulletin(bulletin);
    setFormData({
      title: bulletin.title,
      content: bulletin.content,
      type: bulletin.type
    });
    setImageFile(null);
    // Use the new simpler image URL format
    setImagePreview(bulletin.image_url ? `${API}${bulletin.image_url.replace('/api', '')}` : null);
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ title: "", content: "", type: "info" });
    setImageFile(null);
    setImagePreview(null);
    setShowCreateModal(true);
  };

  const getBulletinIcon = (type) => {
    switch (type) {
      case "critical":
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBulletinStyle = (type) => {
    switch (type) {
      case "critical":
        return "border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20";
      case "warning":
        return "border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-900/20";
      default:
        return "border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20";
    }
  };

  const getBulletinBadge = (type) => {
    switch (type) {
      case "critical":
        return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
      case "warning":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300";
      default:
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300";
    }
  };

  if (loading) {
    return (
      <Card className="border-0 shadow-md dark:bg-dark-card dark:border-dark-border">
        <CardHeader className="border-b border-slate-100 dark:border-dark-border">
          <CardTitle className="flex items-center text-slate-900 dark:text-white">
            <Megaphone className="w-5 h-5 mr-2 text-blue-500" />
            IT Bulletin Board
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center text-slate-500 dark:text-dark-text-muted">Loading bulletins...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-md dark:bg-dark-card dark:border-dark-border" data-testid="bulletin-board">
        <CardHeader className="border-b border-slate-100 dark:border-dark-border">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-slate-900 dark:text-white">
              <Megaphone className="w-5 h-5 mr-2 text-blue-500" />
              IT Bulletin Board
            </CardTitle>
            {canManage && (
              <Button
                onClick={openCreateModal}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-bulletin-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Update
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {bulletins.length === 0 ? (
            <div className="text-center py-8 text-slate-500 dark:text-dark-text-muted">
              <Megaphone className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-dark-text-muted" />
              <p>No bulletins posted yet</p>
              {canManage && (
                <p className="text-sm mt-2">Post updates about system issues or changes</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {bulletins.map((bulletin) => (
                <div
                  key={bulletin.id}
                  className={`p-4 rounded-lg ${getBulletinStyle(bulletin.type)}`}
                  data-testid={`bulletin-${bulletin.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-3 flex-1">
                      {getBulletinIcon(bulletin.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800 dark:text-white">{bulletin.title}</h3>
                          <Badge className={getBulletinBadge(bulletin.type)} size="sm">
                            {bulletin.type}
                          </Badge>
                        </div>
                        <p className="text-slate-700 dark:text-gray-200 whitespace-pre-wrap">{bulletin.content}</p>
                        {bulletin.image_url && (
                          <div className="mt-3">
                            <img 
                              src={`${API}${bulletin.image_url.replace('/api', '')}`}
                              alt="Bulletin attachment"
                              className="max-w-full max-h-64 rounded-lg object-contain bg-white shadow-sm"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 dark:text-dark-text-muted">
                          <span>Posted by <strong className="text-slate-700 dark:text-gray-300">{bulletin.created_by_name}</strong></span>
                          <span>•</span>
                          <span>{new Date(bulletin.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-2 ml-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(bulletin)}
                          data-testid={`edit-bulletin-${bulletin.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedBulletin(bulletin);
                            setShowDeleteDialog(true);
                          }}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-bulletin-${bulletin.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Bulletin Modal */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Post New Bulletin</DialogTitle>
              <DialogDescription>
                Share important updates or current issues with all users
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger data-testid="bulletin-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Brief title for the update"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="bulletin-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  placeholder="Detailed information about the issue or update..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={6}
                  data-testid="bulletin-content-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Attach Image (Optional)</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="Preview" className="max-h-40 rounded-lg border" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-dashed"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Add Image
                  </Button>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={uploading} data-testid="submit-bulletin">
                  {uploading ? "Posting..." : "Post Bulletin"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Bulletin Modal */}
      {showEditModal && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Bulletin</DialogTitle>
              <DialogDescription>Update the bulletin information</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-type">Type</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-content">Content</Label>
                <Textarea
                  id="edit-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={6}
                />
              </div>
              <div className="space-y-2">
                <Label>Image</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                {imagePreview ? (
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="Preview" className="max-h-40 rounded-lg border" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={removeImage}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-dashed"
                  >
                    <ImagePlus className="w-4 h-4 mr-2" />
                    Add Image
                  </Button>
                )}
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={uploading}>
                  {uploading ? "Updating..." : "Update Bulletin"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bulletin?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the bulletin: <strong>{selectedBulletin?.title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-bulletin"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
